// Deterministic PRNG and seamless (periodic) value noise for the procedural texture generators.
// No THREE / DOM dependency: safe to import from tests running in node.

/** Small fast 32-bit PRNG. Returns floats in [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed | 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a hash of a string as an unsigned 32-bit seed. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** One random lattice of `cx` x `cy` values; sampling wraps around both axes. */
interface Lattice {
  readonly cx: number;
  readonly cy: number;
  readonly values: Float32Array;
}

/** Smoothstep-interpolated lookup into a wrapping lattice. u and v are in tile units (period 1). */
function sampleLattice(lat: Lattice, u: number, v: number): number {
  const { cx, cy, values } = lat;
  const x = u * cx;
  const y = v * cy;
  const fx = Math.floor(x);
  const fy = Math.floor(y);
  const tx = x - fx;
  const ty = y - fy;
  let x0 = fx % cx;
  if (x0 < 0) x0 += cx;
  let y0 = fy % cy;
  if (y0 < 0) y0 += cy;
  const x1 = x0 + 1 === cx ? 0 : x0 + 1;
  const y1 = y0 + 1 === cy ? 0 : y0 + 1;
  const sx = tx * tx * (3 - 2 * tx);
  const sy = ty * ty * (3 - 2 * ty);
  const row0 = y0 * cx;
  const row1 = y1 * cx;
  const a = values[row0 + x0];
  const b = values[row0 + x1];
  const c = values[row1 + x0];
  const d = values[row1 + x1];
  const top = a + (b - a) * sx;
  const bottom = c + (d - c) * sx;
  return top + (bottom - top) * sy;
}

/**
 * Seamless value noise: `sample` and `fbm` are periodic with period 1 in both u and v, so a
 * texture generated by evaluating them over [0, 1)^2 tiles without visible seams.
 * Unequal cell counts give anisotropic (stretched) features, e.g. 2 x 96 cells = horizontal streaks.
 */
export class TileNoise {
  private readonly seed: number;
  private readonly cellsX: number;
  private readonly cellsY: number;
  /** Lattice per octave, built on demand; octave k has cellsX * 2^k by cellsY * 2^k cells. */
  private readonly lattices: Lattice[] = [];

  constructor(seed: number, cellsX: number, cellsY: number) {
    this.seed = seed | 0;
    this.cellsX = Math.max(1, Math.round(cellsX));
    this.cellsY = Math.max(1, Math.round(cellsY));
  }

  /** Single-octave noise in 0..1. */
  sample(u: number, v: number): number {
    return sampleLattice(this.lattice(0), u, v);
  }

  /** Fractal sum of `octaves` octaves in 0..1 (normalised by the amplitude sum). */
  fbm(u: number, v: number, octaves: number, gain = 0.5): number {
    let sum = 0;
    let amp = 1;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      sum += amp * sampleLattice(this.lattice(o), u, v);
      norm += amp;
      amp *= gain;
    }
    return norm > 0 ? sum / norm : 0;
  }

  private lattice(octave: number): Lattice {
    while (this.lattices.length <= octave) {
      const o = this.lattices.length;
      const cx = this.cellsX * 2 ** o;
      const cy = this.cellsY * 2 ** o;
      const rand = mulberry32(this.seed + Math.imul(o + 1, 0x9e3779b1));
      const values = new Float32Array(cx * cy);
      for (let i = 0; i < values.length; i++) values[i] = rand();
      this.lattices.push({ cx, cy, values });
    }
    return this.lattices[octave];
  }
}
